<template>
  <div v-if="enabled || historyLoading || loadError || (isAdmin && metrics)" class="invite-panel" aria-labelledby="volunteer-invite-title">
    <div class="invite-panel-header">
      <div class="invite-panel-mark" aria-hidden="true">
        <i class="material-icons">volunteer_activism</i>
      </div>
      <div class="invite-panel-heading">
        <p class="invite-eyebrow">A little invitation can open a big door</p>
        <h2 id="volunteer-invite-title">Invite someone to serve with you</h2>
        <p class="invite-intro">Share a role you love, or simply invite them to explore ways to help.</p>
      </div>
      <div class="invite-header-note d-none d-lg-flex">
        <i class="material-icons" aria-hidden="true">favorite_border</i>
        <span>Made for sharing</span>
      </div>
    </div>

    <div v-if="loadError" class="invite-alert invite-alert-error" role="alert">{{ loadError }}</div>
    <div v-else-if="successMessage" class="invite-alert invite-alert-success" role="status">
      <i class="material-icons" aria-hidden="true">check_circle</i>
      <span>{{ successMessage }}</span>
      <button type="button" class="invite-dismiss" aria-label="Dismiss message" @click="successMessage = ''">×</button>
    </div>

    <section v-if="isAdmin && metrics" class="invite-impact-summary" aria-label="Volunteer invitation results">
      <div><strong>{{ metrics.signed_up }}</strong><span>volunteers signed up from invitations</span></div>
      <div><strong>{{ metrics.sent }}</strong><span>invitations sent</span></div>
    </section>

    <div v-if="enabled" class="invite-panel-body">
      <form class="invite-form" @submit.prevent="sendInvite">
        <div class="invite-form-topline">
          <h3><span>01</span> Who would you love to serve alongside?</h3>
          <p>We’ll send a personal invitation from you. Replies come to your email.</p>
        </div>

        <div class="row g-3">
          <div class="col-md-4">
            <label class="invite-label" for="invite-firstname">First name <span>*</span></label>
            <input id="invite-firstname" v-model.trim="form.firstname" class="form-control invite-input"
              type="text" maxlength="100" autocomplete="given-name" required />
          </div>
          <div class="col-md-4">
            <label class="invite-label" for="invite-lastname">Last name</label>
            <input id="invite-lastname" v-model.trim="form.lastname" class="form-control invite-input"
              type="text" maxlength="100" autocomplete="family-name" />
          </div>
          <div class="col-md-4">
            <label class="invite-label" for="invite-email">Email address <span>*</span></label>
            <input id="invite-email" v-model.trim="form.email" class="form-control invite-input"
              type="email" maxlength="254" autocomplete="email" required />
          </div>
        </div>

        <fieldset v-if="skills.length" class="invite-skills mt-4">
          <legend><span>02</span> What might they enjoy?</legend>
          <p class="invite-field-hint">Choose any roles you think could be a good fit. This is optional.</p>
          <div class="invite-skill-list">
            <label v-for="skill in skills" :key="skill.id" class="invite-skill-option">
              <input v-model="form.skill_ids" type="checkbox" :value="skill.id" />
              <span class="invite-skill-check" aria-hidden="true"><i class="material-icons">check</i></span>
              <span class="invite-skill-copy">
                <strong>{{ skill.name }}</strong>
                <small v-if="skill.description">{{ skill.description }}</small>
              </span>
            </label>
          </div>
        </fieldset>

        <div class="invite-message mt-4">
          <label class="invite-label" for="invite-note"><span class="invite-step">03</span> Add a personal note <span class="invite-optional">Optional</span></label>
          <textarea id="invite-note" v-model.trim="form.custom_message" class="form-control invite-input"
            rows="3" maxlength="2000" placeholder="What do you enjoy about serving? What would you like them to know?"></textarea>
          <div class="invite-character-count">{{ form.custom_message.length }}/2000</div>
        </div>

        <div class="invite-form-footer">
          <p><i class="material-icons" aria-hidden="true">lock</i> Their details are only used to send this invitation.</p>
          <button class="btn invite-send-button" type="submit" :disabled="sending">
            <span v-if="sending" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <i v-else class="material-icons" aria-hidden="true">send</i>
            {{ sending ? 'Sending invitation…' : 'Send invitation' }}
          </button>
        </div>
      </form>

      <section class="invite-history" aria-labelledby="invite-history-title">
        <div class="invite-history-header">
          <div>
            <p class="invite-eyebrow">Your invitations</p>
            <h3 id="invite-history-title">A little encouragement goes a long way</h3>
          </div>
          <span class="invite-history-count">{{ totalInvitations }} sent</span>
        </div>

        <div v-if="historyLoading" class="invite-history-loading" role="status">
          <span class="spinner-border spinner-border-sm text-success" aria-hidden="true"></span> Loading your invitations…
        </div>
        <div v-else-if="invitations.length === 0" class="invite-empty-state">
          <div class="invite-empty-icon"><i class="material-icons" aria-hidden="true">mail_outline</i></div>
          <p class="mb-1">Your first invitation starts here.</p>
          <small>When you share an invitation, it will appear here.</small>
        </div>
        <div v-else class="invite-history-list">
          <article v-for="invite in invitations" :key="invite.id" class="invite-history-item">
            <div class="invite-history-avatar" aria-hidden="true">{{ initials(invite) }}</div>
            <div class="invite-history-person">
              <strong>{{ invite.firstname }} {{ invite.lastname }}</strong>
              <span>{{ invite.email }}</span>
              <small v-if="invite.skills?.length">Suggested: {{ invite.skills.join(', ') }}</small>
            </div>
            <div class="invite-history-meta">
              <span class="invite-status" :class="`is-${invite.status}`">{{ statusLabel(invite.status) }}</span>
              <time v-if="invite.sent_at || invite.created_at">{{ formatDate(invite.sent_at || invite.created_at) }}</time>
            </div>
          </article>
          <div v-if="lastPage > 1" class="invite-pagination">
            <button type="button" :disabled="page <= 1 || historyLoading" @click="loadHistory(page - 1)">Previous</button>
            <span>Page {{ page }} of {{ lastPage }}</span>
            <button type="button" :disabled="page >= lastPage || historyLoading" @click="loadHistory(page + 1)">Next</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/config/api';

export default {
  name: 'VolunteerInviteCard',
  props: {
    locationId: { type: [String, Number], required: true },
  },
  emits: ['feature-status'],
  data() {
    return {
      enabled: false,
      isAdmin: false,
      metrics: null,
      skills: [],
      invitations: [],
      historyLoading: true,
      sending: false,
      loadError: '',
      successMessage: '',
      page: 1,
      lastPage: 1,
      totalInvitations: 0,
      form: {
        firstname: '',
        lastname: '',
        email: '',
        skill_ids: [],
        custom_message: '',
      },
    };
  },
  mounted() {
    this.loadHistory(1);
  },
  methods: {
    async loadHistory(page) {
      this.historyLoading = true;
      this.loadError = '';
      try {
        const response = await api.get('/volunteer-invitations', {
          params: { location_id: this.locationId, page },
        });
        const data = response.data?.data;
        this.enabled = data?.enabled === true;
        this.isAdmin = data?.is_admin === true;
        this.metrics = data?.metrics || null;
        this.$emit('feature-status', this.enabled);
        this.skills = data?.skills || [];
        this.invitations = data?.invitations || [];
        this.page = data?.pagination?.current_page || 1;
        this.lastPage = data?.pagination?.last_page || 1;
        this.totalInvitations = response.data?.data?.pagination?.total ?? this.invitations.length;
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Invitations could not be loaded right now.';
        this.$emit('feature-status', false);
      } finally {
        this.historyLoading = false;
      }
    },
    async sendInvite() {
      if (this.sending) return;
      this.sending = true;
      this.loadError = '';
      this.successMessage = '';
      try {
        const response = await api.post('/volunteer-invitations', {
          location_id: this.locationId,
          firstname: this.form.firstname,
          lastname: this.form.lastname,
          email: this.form.email,
          skill_ids: this.form.skill_ids,
          custom_message: this.form.custom_message,
        });
        const skipped = response.data?.data?.skipped_skill_names || [];
        this.successMessage = skipped.length
          ? `${response.data?.message || 'Invitation sent.'} ${skipped.join(', ')} ${skipped.length === 1 ? 'is' : 'are'} already on their serving list.`
          : (response.data?.message || 'Your invitation has been sent.');
        this.form = { firstname: '', lastname: '', email: '', skill_ids: [], custom_message: '' };
        await this.loadHistory(1);
      } catch (error) {
        this.loadError = error.response?.data?.message || 'We could not send this invitation. Please try again.';
      } finally {
        this.sending = false;
      }
    },
    initials(invite) {
      return `${invite.firstname?.trim()?.[0] || ''}${invite.lastname?.trim()?.[0] || ''}`.toUpperCase() || '•';
    },
    statusLabel(status) {
      return ({ sent: 'Sent', pending: 'Sending', failed: 'Not sent', registered: 'Joined', accepted: 'Interested' })[status] || 'Sent';
    },
    formatDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
    },
  },
};
</script>

<style scoped>
.invite-panel {
  overflow: hidden;
  border: 1px solid #d9e7dd;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 26px rgba(27, 75, 52, 0.08);
  color: #263a30;
}
.invite-panel-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 1.6rem;
  background: linear-gradient(105deg, #edf7ef 0%, #f8faf6 58%, #fff 100%);
  border-bottom: 1px solid #e3ece5;
}
.invite-panel-mark {
  display: grid;
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  place-items: center;
  border-radius: 14px;
  background: #176b4d;
  color: #fff;
}
.invite-panel-mark .material-icons { font-size: 26px; }
.invite-panel-heading { min-width: 0; }
.invite-eyebrow {
  margin: 0 0 0.25rem;
  color: #538267;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.invite-panel-heading h2 {
  margin: 0;
  color: #214d37;
  font-size: 1.25rem;
  font-weight: 750;
}
.invite-intro { margin: 0.3rem 0 0; color: #66756b; font-size: 0.88rem; }
.invite-header-note {
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
  color: #668371;
  font-size: 0.78rem;
  white-space: nowrap;
}
.invite-header-note .material-icons { font-size: 18px; }
.invite-panel-body { padding: 1.45rem 1.6rem 1.35rem; }
.invite-impact-summary { display: flex; flex-wrap: wrap; gap: 1.5rem; padding: .85rem 1.6rem; border-bottom: 1px solid #e3ece5; background: #f5f9f5; }
.invite-impact-summary > div { display: flex; align-items: baseline; gap: .5rem; }
.invite-impact-summary strong { color: #176b4d; font-size: 1.1rem; }
.invite-impact-summary span { color: #617268; font-size: .78rem; }
.invite-form-topline h3,
.invite-skills legend,
.invite-history-header h3 {
  margin: 0;
  color: #30483a;
  font-size: 0.96rem;
  font-weight: 700;
}
.invite-form-topline h3 > span,
.invite-skills legend > span,
.invite-step {
  display: inline-grid;
  width: 22px;
  height: 22px;
  margin-right: 0.4rem;
  place-items: center;
  border-radius: 50%;
  background: #e8f3eb;
  color: #28704b;
  font-size: 0.68rem;
  font-weight: 800;
  vertical-align: middle;
}
.invite-form-topline > p,
.invite-field-hint { margin: 0.35rem 0 1rem 1.95rem; color: #76837a; font-size: 0.78rem; }
.invite-label { display: block; margin-bottom: 0.4rem; color: #3f5346; font-size: 0.78rem; font-weight: 700; }
.invite-label > span:first-child { color: #bc4d3d; }
.invite-input { min-height: 43px; border-color: #dbe5dd; border-radius: 6px; color: #263a30; font-size: 0.88rem; }
.invite-input:focus { border-color: #64a47a; box-shadow: 0 0 0 3px rgba(52, 128, 78, 0.12); }
.invite-skills { min-width: 0; margin: 0; padding: 0; border: 0; }
.invite-skills legend { float: none; width: auto; margin: 0; }
.invite-skill-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.55rem; }
.invite-skill-option {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 58px;
  align-items: center;
  gap: 0.7rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid #e2e9e3;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.invite-skill-option:hover { border-color: #99c2a3; background: #fbfdfb; }
.invite-skill-option > input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.invite-skill-check { display: grid; width: 19px; height: 19px; flex: 0 0 19px; place-items: center; border: 1px solid #b7c7ba; border-radius: 4px; color: transparent; }
.invite-skill-check .material-icons { font-size: 15px; }
.invite-skill-option > input:checked + .invite-skill-check { border-color: #28704b; background: #28704b; color: #fff; }
.invite-skill-option > input:focus-visible + .invite-skill-check { outline: 3px solid rgba(40, 112, 75, 0.2); }
.invite-skill-copy { min-width: 0; }
.invite-skill-copy strong { display: block; overflow-wrap: anywhere; color: #344a3a; font-size: 0.82rem; }
.invite-skill-copy small { display: block; margin-top: 0.12rem; color: #78867c; font-size: 0.72rem; }
.invite-step { margin-right: 0.25rem; }
.invite-optional { float: right; color: #869188 !important; font-size: 0.72rem; font-weight: 500; }
.invite-message { position: relative; }
.invite-message textarea { min-height: 86px; padding-right: 4.7rem; resize: vertical; }
.invite-character-count { position: absolute; right: 0.65rem; bottom: 0.55rem; color: #8a968c; font-size: 0.68rem; }
.invite-form-footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #edf1ed; }
.invite-form-footer p { display: flex; align-items: center; gap: 0.35rem; margin: 0; color: #78857c; font-size: 0.72rem; }
.invite-form-footer p .material-icons { font-size: 15px; }
.invite-send-button { display: inline-flex; min-height: 42px; align-items: center; justify-content: center; gap: 0.5rem; padding: 0.55rem 1rem; border: 0; border-radius: 5px; background: #176b4d; color: #fff; font-size: 0.82rem; font-weight: 700; }
.invite-send-button:hover:not(:disabled) { background: #11583e; color: #fff; }
.invite-send-button:disabled { cursor: wait; opacity: 0.7; }
.invite-send-button .material-icons { font-size: 17px; }
.invite-history { margin-top: 1.55rem; padding-top: 1.2rem; border-top: 1px solid #e8eee9; }
.invite-history-header { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.75rem; }
.invite-history-count { color: #78867c; font-size: 0.74rem; white-space: nowrap; }
.invite-empty-state { display: flex; min-height: 84px; align-items: center; gap: 0.85rem; padding: 0.8rem 0.9rem; border: 1px dashed #d8e3da; border-radius: 6px; background: #fbfdfb; }
.invite-empty-icon { display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center; border-radius: 50%; background: #eaf3ec; color: #568367; }
.invite-empty-icon .material-icons { font-size: 20px; }
.invite-empty-state p { color: #455b4b; font-size: 0.8rem; font-weight: 700; }
.invite-empty-state small { color: #849087; font-size: 0.73rem; }
.invite-history-list { border-top: 1px solid #edf1ed; }
.invite-history-item { display: flex; min-width: 0; align-items: center; gap: 0.75rem; padding: 0.72rem 0; border-bottom: 1px solid #edf1ed; }
.invite-history-avatar { display: grid; width: 36px; height: 36px; flex: 0 0 36px; place-items: center; border-radius: 50%; background: #e6f0e8; color: #346b49; font-size: 0.7rem; font-weight: 800; }
.invite-history-person { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 0.1rem; }
.invite-history-person strong { overflow: hidden; color: #364a3c; font-size: 0.79rem; text-overflow: ellipsis; white-space: nowrap; }
.invite-history-person span,.invite-history-person small { overflow: hidden; color: #7d8980; font-size: 0.7rem; text-overflow: ellipsis; white-space: nowrap; }
.invite-history-meta { display: flex; flex: 0 0 auto; flex-direction: column; align-items: flex-end; gap: 0.25rem; }
.invite-history-meta time { color: #89938b; font-size: 0.67rem; }
.invite-status { padding: 0.18rem 0.45rem; border-radius: 999px; background: #edf3ee; color: #55705c; font-size: 0.66rem; font-weight: 700; }
.invite-status.is-failed { background: #fbeceb; color: #a7473e; }
.invite-status.is-registered,.invite-status.is-accepted { background: #e2f3e8; color: #247147; }
.invite-pagination { display: flex; align-items: center; justify-content: center; gap: 0.8rem; padding-top: 0.8rem; color: #77847b; font-size: 0.72rem; }
.invite-pagination button { padding: 0.2rem 0.4rem; border: 0; background: transparent; color: #28704b; font-size: inherit; font-weight: 700; }
.invite-pagination button:disabled { color: #a4ada6; }
.invite-alert { display: flex; align-items: center; gap: 0.5rem; margin: 1rem 1.6rem 0; padding: 0.7rem 0.85rem; border-radius: 5px; font-size: 0.8rem; }
.invite-alert-error { background: #fff0ed; color: #9a3e32; }
.invite-alert-success { background: #edf8ef; color: #28704b; }
.invite-dismiss { margin-left: auto; border: 0; background: transparent; color: inherit; font-size: 1.25rem; line-height: 1; }
@media (max-width: 767.98px) {
  .invite-panel-header { padding: 1.1rem; }
  .invite-panel-mark { width: 46px; height: 46px; flex-basis: 46px; }
  .invite-panel-heading h2 { font-size: 1.08rem; }
  .invite-intro { font-size: 0.8rem; }
  .invite-panel-body { padding: 1.1rem; }
  .invite-skill-list { grid-template-columns: 1fr; }
  .invite-form-footer { align-items: stretch; flex-direction: column; }
  .invite-send-button { width: 100%; }
  .invite-alert { margin-right: 1.1rem; margin-left: 1.1rem; }
}
@media (prefers-reduced-motion: reduce) {
  .invite-skill-option,.invite-input,.invite-send-button { transition: none; }
}
</style>